import { useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';

import { EnquiryFormPageType, EnquiryFormTrigger } from 'generated/graphql';
import type { PublicEnquiryForm } from '~/modules/enquiry/types/public-enquiry-form';
import { enquiryFormForPageUrl } from '~/modules/enquiry/hooks/use-enquiry-form-for-page';
import {
    ENQUIRY_FORM_API_ROUTE,
    type EnquiryFormApiResponse,
} from '~/routes/remix-api.enquiry-form';
import type { BookMeetingGateIntent } from '../book-meeting-gate-state';

export interface BookMeetingFormLookup {
    /** The form whose questions and routing the booking follows, if any. */
    form: PublicEnquiryForm | null;
    /** Whose form it is: a team member's own, or the page's Book button's. */
    source: 'personal' | 'page' | null;
    /** True until a lookup the gate is waiting on has answered. */
    isLoading: boolean;
}

/**
 * Where the gate reads the form from, most specific first: the person's own
 * booking form, then the form the page already resolved, then the page form by
 * id. Null when the CTA carries no form at all, which is also the whole story
 * with Forms switched off.
 */
export const bookMeetingFormUrl = (
    intent: Pick<
        BookMeetingGateIntent,
        | 'bookingFormId'
        | 'preferredTeamMemberId'
        | 'formId'
        | 'formConfig'
        | 'professionalId'
        | 'pageType'
        | 'siteTreeId'
    >,
    { skipPersonal = false }: { skipPersonal?: boolean } = {},
): string | null => {
    if (!skipPersonal && intent.bookingFormId && intent.preferredTeamMemberId) {
        return `${ENQUIRY_FORM_API_ROUTE}?teamMemberId=${intent.preferredTeamMemberId}`;
    }
    if (intent.formConfig) return null;
    if (intent.formId && intent.professionalId && intent.pageType) {
        return enquiryFormForPageUrl({
            professionalId: intent.professionalId,
            pageType: EnquiryFormPageType[intent.pageType],
            trigger: EnquiryFormTrigger.BookMeeting,
            productId: intent.siteTreeId,
        });
    }
    return null;
};

/**
 * The fetcher's data, but only once it answers `url`. A fetcher keeps its last
 * response while it loads the next one, so a change of person (or page form)
 * would otherwise read the previous lookup's form as this one's answer.
 */
const answerFor = (
    fetcher: { state: string; data: EnquiryFormApiResponse | undefined },
    loadedUrl: string | null,
    url: string | null,
): EnquiryFormApiResponse | undefined =>
    url !== null && loadedUrl === url && fetcher.state === 'idle'
        ? fetcher.data
        : undefined;

const formFromConfig = (
    config: BookMeetingGateIntent['formConfig'],
): PublicEnquiryForm | null =>
    config?.status === 'form' ? (config.form ?? null) : null;

/**
 * Resolves the Business Manager form a booking follows.
 *
 * A person's own form wins; when they turn out to have none (removed, or Forms
 * off since the page rendered) the page's form is used instead, so the
 * questions the practice set for its Book button still run.
 */
export function useBookMeetingForm(
    intent: BookMeetingGateIntent | null,
    { enabled = true }: { enabled?: boolean } = {},
): BookMeetingFormLookup {
    const personalFetcher = useFetcher<EnquiryFormApiResponse>();
    const pageFetcher = useFetcher<EnquiryFormApiResponse>();

    const personalUrl =
        intent?.bookingFormId && intent.preferredTeamMemberId
            ? bookMeetingFormUrl(intent)
            : null;
    const pageUrl = intent
        ? bookMeetingFormUrl(intent, { skipPersonal: true })
        : null;

    const personalLoaded = useRef<string | null>(null);
    useEffect(() => {
        if (!enabled || !personalUrl) return;
        if (personalLoaded.current === personalUrl) return;
        personalLoaded.current = personalUrl;
        personalFetcher.load(personalUrl);
        // The fetcher's own state changes must not restart the load.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, personalUrl]);

    const personalAnswer = answerFor(
        personalFetcher,
        personalLoaded.current,
        personalUrl,
    );
    const personalForm = formFromConfig(personalAnswer);
    const personalAnswered = personalAnswer !== undefined;
    const needsPageForm = !personalUrl || (personalAnswered && !personalForm);

    const pageLoaded = useRef<string | null>(null);
    useEffect(() => {
        if (!enabled || !pageUrl || !needsPageForm) return;
        if (pageLoaded.current === pageUrl) return;
        pageLoaded.current = pageUrl;
        pageFetcher.load(pageUrl);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, pageUrl, needsPageForm]);

    if (!intent) return { form: null, source: null, isLoading: false };

    if (personalUrl && !personalAnswered) {
        return { form: null, source: null, isLoading: enabled };
    }
    if (personalForm) {
        return { form: personalForm, source: 'personal', isLoading: false };
    }

    const configured = formFromConfig(intent.formConfig);
    if (configured)
        return { form: configured, source: 'page', isLoading: false };
    if (!pageUrl) return { form: null, source: null, isLoading: false };
    const pageAnswer = answerFor(pageFetcher, pageLoaded.current, pageUrl);
    if (pageAnswer === undefined) {
        return { form: null, source: null, isLoading: enabled };
    }
    const pageForm = formFromConfig(pageAnswer);
    return {
        form: pageForm,
        source: pageForm ? 'page' : null,
        isLoading: false,
    };
}
